import * as THREE from "three";
import { buildLeafHomes } from "../legacy/leafGeometry";
import { curl, hash, TAU } from "../systems/math";
import type { QualityPreset } from "../systems/quality";
import { tokens } from "../brand/tokens";

type Particle = {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  homeX: number;
  homeY: number;
  homeZ: number;
  role: string;
  leaflet: number;
  phase: number;
  size: number;
};

export class CanopyField {
  readonly group = new THREE.Group();
  private mesh: THREE.InstancedMesh | null = null;
  private particles: Particle[] = [];
  private dummy = new THREE.Object3D();
  private time = 0;
  private quality: QualityPreset;
  private color = new THREE.Color(tokens.leaf);
  private fogColor = new THREE.Color(tokens.fog);

  constructor(quality: QualityPreset) {
    this.quality = quality;
    this.group.name = "CanopyField";
    if (quality.particleScale <= 0) return;
    this.build();
  }

  private build() {
    const homes = buildLeafHomes();
    const scale = Math.max(0.15, this.quality.particleScale);
    const budget = Math.floor(homes.length * scale);

    this.particles = homes.slice(0, budget).map((p, i) => {
      const z = (hash(i * 3.7) - 0.5) * 0.35;
      return {
        x: p.x,
        y: p.y,
        z,
        vx: 0,
        vy: 0,
        homeX: p.x,
        homeY: p.y,
        homeZ: z,
        role: p.role,
        leaflet: p.leaflet,
        phase: hash(i * 5.13 + 2.1) * TAU,
        size:
          p.role === "hub"
            ? 0.045
            : p.role === "tip"
              ? 0.032
              : p.role === "waist"
                ? 0.026
                : 0.018,
      };
    });

    const geo = new THREE.SphereGeometry(1, 8, 8);
    const mat = new THREE.MeshStandardMaterial({
      color: this.color,
      emissive: this.color,
      emissiveIntensity: 0.35,
      roughness: 0.45,
      metalness: 0.05,
      transparent: true,
      opacity: 0.92,
    });

    this.mesh = new THREE.InstancedMesh(geo, mat, this.particles.length);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.group.add(this.mesh);
    this.group.scale.setScalar(1.65);
    this.group.position.set(0, 0.15, 0);
  }

  setProgress(progress: number) {
    // Canopy densifies mid-journey, contracts near threshold
    const breathe = 1 + Math.sin(progress * Math.PI) * 0.12;
    this.group.scale.setScalar(1.55 * breathe);
    const mat = this.mesh?.material as THREE.MeshStandardMaterial | undefined;
    if (mat) {
      mat.emissiveIntensity = 0.25 + progress * 0.55;
    }
  }

  update(dt: number) {
    if (!this.mesh || this.quality.reducedMotion) return;

    this.time += dt;
    const spring = 2.8;

    this.particles.forEach((p, i) => {
      const wave =
        p.leaflet >= 0
          ? Math.sin(this.time * 1.7 + p.leaflet * 0.85 + p.phase) * 0.016
          : 0;
      const flow = curl(p.x * 1.7, p.y * 1.7, this.time * 0.25 + p.phase * 0.05);
      const orbit = 0.02 * Math.sin(this.time * 0.7 + p.phase);
      const ax = (p.homeX - p.x) * spring + flow.x * 0.055 - p.y * orbit;
      const ay = (p.homeY - p.y) * spring + flow.y * 0.055 + p.x * orbit * 0.4 + wave;

      p.vx = p.vx * 0.9 + ax * dt;
      p.vy = p.vy * 0.9 + ay * dt;
      p.x += p.vx;
      p.y += p.vy;
      p.z = p.homeZ + Math.sin(this.time * 0.9 + p.phase) * 0.03;

      if (p.role === "hub") {
        p.x = 0;
        p.vx = 0;
      }

      const glow = 0.85 + Math.sin(this.time * 2.4 + p.phase) * 0.15;
      const s = p.size * glow;

      this.dummy.position.set(p.x, p.y, p.z);
      this.dummy.scale.setScalar(s);
      this.dummy.updateMatrix();
      this.mesh!.setMatrixAt(i, this.dummy.matrix);

      const c = p.role === "hub" ? this.color : this.fogColor;
      this.mesh!.setColorAt?.(i, c);
    });

    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  dispose() {
    this.mesh?.geometry.dispose();
    (this.mesh?.material as THREE.Material | undefined)?.dispose();
  }
}
